/**
 * Edition-Aware Purchase Logic
 * Handles purchasing editions from master pieces
 */

import { purchaseListing } from './resaleMarketplace';
import { completePurchaseWithDelegate } from './marketplaceTransactions';
import { getAvailableEditionForPurchase } from './editionMinting';
import { supabase } from '../lib/supabase';

// Scalability utilities
import { trackTransaction } from './performanceMonitor';

/**
 * Purchase a piece (automatically selects available edition if master)
 * This is the main entry point for buying pieces in the marketplace
 * 
 * @param {Object} wallet - Connected wallet adapter
 * @param {Object} piece - Master piece object (from marketplace)
 * @param {number} price - Purchase price in SOL
 * @returns {Promise<Object>} Purchase result
 */
export async function purchasePiece(wallet, piece, price) {
  if (!wallet.connected || !wallet.publicKey) {
    throw new Error('Wallet not connected');
  }

  // Track performance
  const startTime = Date.now();

  console.log('🛒 Starting piece purchase');
  console.log('  - Piece:', piece.name);
  console.log('  - Price:', price, 'SOL');
  console.log('  - Is Master:', piece.is_master);
  console.log('  - Total Quantity:', piece.total_quantity);
  console.log('  - Available:', piece.available_count);

  try {
    // Step 1: Extract numeric ID from potentially prefixed ID (e.g., "supabase_128" -> 128)
    let numericPieceId = piece.id;
    const pieceIdString = String(piece.id);
    if (pieceIdString.startsWith('supabase_')) {
      numericPieceId = parseInt(pieceIdString.replace('supabase_', ''), 10);
    } else if (pieceIdString.startsWith('local_')) {
      // For local pieces, extract the middle number (e.g., "local_456_0" -> 456)
      const match = pieceIdString.match(/local_(\d+)_/);
      if (match) {
        numericPieceId = parseInt(match[1], 10);
      }
    } else if (!isNaN(pieceIdString)) {
      numericPieceId = parseInt(pieceIdString, 10);
    }
    
    console.log('🆔 Piece ID conversion:', { original: piece.id, numeric: numericPieceId });
    
    // Step 2: Get piece to purchase (simplified - all pieces have mint_address)
    console.log('📦 Purchasing piece directly');
    
    const pieceToPurchase = {
      pieceId: numericPieceId,
      mintAddress: piece.mint_address,
      price: price,
      holderWallet: piece.holder_wallet
    };
    
    // Verify piece has a mint address
    if (!pieceToPurchase.mintAddress) {
      throw new Error('This piece is not yet minted and cannot be purchased');
    }

    // Step 3: Check if there's an active listing (optional for primary sales)
    console.log('🔍 Checking for active listing...');
    let listing = null;
    
    try {
      const { data, error: listingError } = await supabase
        .from('listings')
        .select('*')
        .eq('mint_address', pieceToPurchase.mintAddress)
        .eq('status', 'active')
        .maybeSingle();

      if (listingError && listingError.code !== 'PGRST116') {
        console.warn('⚠️ Could not check listing (table may not exist):', listingError.message);
        // Don't throw - we can still do primary sales without listings
      } else {
        listing = data;
      }
    } catch (err) {
      console.warn('⚠️ Listing check failed, continuing with direct purchase:', err.message);
      // Continue without listing - allows primary sales to work
    }

    // Step 4: Get the piece data
    const { data: pieceData, error: pieceError } = await supabase
      .from('pieces')
      .select(`
        *,
        creators (
          id,
          wallet,
          handle,
          is_verified,
          profile_picture_url
        )
      `)
      .eq('id', pieceToPurchase.pieceId)
      .maybeSingle();

    if (pieceError) {
      console.error('Error fetching piece:', pieceError);
      throw new Error('Failed to fetch piece data');
    }

    console.log('✅ Found piece:', pieceData.name);

    // Step 5: Execute purchase based on listing type
    let purchaseResult;

    if (listing) {
      // Use listing-based purchase flow (newer delegate system)
      console.log('💰 Purchasing via listing system...');
      
      purchaseResult = await purchaseListing(
        wallet,
        pieceData,
        listing,
        pieceToPurchase.price
      );
      
    } else {
      // Use direct purchase flow (delegate-based transfer)
      console.log('💰 Purchasing via direct transfer...');
      
      purchaseResult = await completePurchaseWithDelegate(
        wallet,
        pieceData,
        pieceToPurchase.price
      );
    }

    console.log('✅ Purchase successful!');

    // Track success
    trackTransaction('purchase', startTime, true);

    return {
      ...purchaseResult,
      pieceId: pieceToPurchase.pieceId
    };

  } catch (error) {
    console.error('❌ Purchase failed:', error);
    
    // Track failure
    trackTransaction('purchase', startTime, false, error.message);
    
    // Provide helpful error messages
    if (error.message.includes('No editions available')) {
      throw error; // Already has a good message
    }
    
    if (error.message.includes('already own')) {
      throw new Error('You already own this piece');
    }
    
    if (error.message.includes('insufficient funds')) {
      throw new Error('Insufficient SOL for purchase. Please add more SOL to your wallet.');
    }
    
    if (error.message.includes('Delegate authorization')) {
      throw new Error('This listing has expired. Please refresh the page to see updated listings.');
    }
    
    throw error;
  }
}

/**
 * Check if a piece can be purchased
 * @param {Object} piece - Master piece object
 * @param {Object} wallet - Connected wallet adapter
 * @returns {Promise<Object>} { canPurchase, reason, availableEditions }
 */
export async function checkPurchaseEligibility(piece, wallet) {
  try {
    // Check if piece is a master
    if (!piece.is_master) {
      return {
        canPurchase: false,
        reason: 'This is not a master piece'
      };
    }

    // Check if piece has available editions
    if (!piece.available_count || piece.available_count === 0) {
      return {
        canPurchase: false,
        reason: 'Sold out'
      };
    }

    // Check if wallet is connected
    if (!wallet || !wallet.connected || !wallet.publicKey) {
      return {
        canPurchase: false,
        reason: 'Wallet not connected'
      };
    }

    // Check if buyer is trying to buy their own piece
    const walletAddress = wallet.publicKey.toString();
    
    // Get an available edition to check ownership
    const availableEdition = await getAvailableEditionForPurchase(piece.id);
    
    if (!availableEdition) {
      return {
        canPurchase: false,
        reason: 'No editions currently available'
      };
    }

    if (availableEdition.holderWallet === walletAddress) {
      return {
        canPurchase: false,
        reason: 'You own this piece'
      };
    }

    // Check if piece is properly listed
    if (piece.listing_status !== 'listed') {
      return {
        canPurchase: false,
        reason: 'Not listed for sale'
      };
    }

    return {
      canPurchase: true,
      reason: null,
      availableEdition
    };

  } catch (error) {
    console.error('Error checking purchase eligibility:', error);
    return {
      canPurchase: false,
      reason: 'Error checking availability'
    };
  }
}

/**
 * Reserve an edition for purchase (prevents concurrent purchase conflicts)
 * This is an optimistic lock - not database-enforced
 * 
 * @param {number} masterPieceId - Master piece ID
 * @param {string} buyerWallet - Buyer's wallet address
 * @returns {Promise<Object|null>} Reserved edition or null
 */
export async function reserveEdition(masterPieceId, buyerWallet) {
  try {
    console.log('🔒 Attempting to reserve edition for buyer:', buyerWallet);
    
    // Get available edition
    const edition = await getAvailableEditionForPurchase(masterPieceId);
    
    if (!edition) {
      return null;
    }

    // Note: In a high-concurrency environment, you might want to add a 
    // 'reserved_by' and 'reserved_at' column to track reservations
    // For now, we rely on the purchase transaction happening quickly
    
    console.log('✅ Edition reserved:', edition.editionNumber);
    return edition;

  } catch (error) {
    console.error('Error reserving edition:', error);
    return null;
  }
}

/**
 * Handle concurrent purchase attempt (show "Just sold" message)
 * @param {Object} piece - Master piece
 * @returns {Promise<boolean>} True if piece just sold out
 */
export async function checkIfJustSoldOut(piece) {
  try {
    // Refresh piece data
    const { data: refreshedPiece, error } = await supabase
      .from('pieces')
      .select('available_count, sold_count')
      .eq('id', piece.id)
      .maybeSingle();

    if (error) {
      return false;
    }

    // Check if available count decreased
    return refreshedPiece.available_count < piece.available_count;

  } catch (error) {
    console.error('Error checking sold out status:', error);
    return false;
  }
}

/**
 * Get purchase summary for display
 * @param {Object} piece - Master piece
 * @param {number} price - Purchase price
 * @returns {Object} Purchase summary
 */
export function getPurchaseSummary(piece, price) {
  const isEdition = piece.total_quantity > 1;
  const platformFeeRate = 0.025; // 2.5%
  const platformFee = price * platformFeeRate;
  const sellerReceives = price - platformFee;

  return {
    pieceName: piece.name,
    isEdition,
    totalEditions: piece.total_quantity,
    availableCount: piece.available_count,
    price,
    platformFee,
    platformFeeRate,
    sellerReceives,
    totalCost: price,
    editionLabel: isEdition ? `Edition of ${piece.total_quantity}` : '1/1'
  };
}

export default {
  purchasePiece,
  checkPurchaseEligibility,
  reserveEdition,
  checkIfJustSoldOut,
  getPurchaseSummary
};

